import { IPublicClientApplication } from '@azure/msal-browser'
import { toast } from 'react-toastify'

import { localStorageConstants } from 'constants/localstorageConstants'
import { B2CError } from 'modules/pages/login/Login'

type StorageKey = keyof typeof localStorageConstants

/**
 * Contain helper function to avoid mulptiple scripts
 * @category helper
 * @module helper
 */

export type ValueOf<T> = T[keyof T]

export type AnyObject = Record<string, unknown>

export function emptyArray<T>(): T[] {
  return []
}

/**
 * Get from localstorage
 * @param key
 */
export const getFromStorage = (key: StorageKey, defaultValue = '') => {
  try {
    const getData = localStorage.getItem(key)
    return getData === null ? defaultValue : JSON.parse(getData)
  } catch (error) {
    return defaultValue
  }
}

/**
 * Set in local storage
 * @param key
 * @param item
 */
export const setInStorage = (key: StorageKey, item: unknown = '') => {
  if (!item) {
    localStorage.setItem(key, `${item}`)
  } else {
    localStorage.setItem(key, JSON.stringify(item))
  }
}

/**
 * Remove from local storage
 * @param key
 */
export const removeFromStorage = (key: StorageKey) => {
  localStorage.removeItem(key)
}

/**
 * Function used to delete a blob
 * @param url The URL of the blob to delete, including the SAS token
 */
export const deleteBlob = async (url: string): Promise<void> => {
  try {
    const response = await fetch(url, {
      method: 'DELETE',
    })
    if (response?.ok) {
      console.log('Blob deleted successfully.')
    } else {
      console.error('Error deleting blob:', response?.statusText)
    }
  } catch (error) {
    console.error('Error deleting blob:', error)
  }
}

export const siteLogout = async (instance: IPublicClientApplication) => {
  try {
    localStorage.clear()
    sessionStorage.clear()
    const keys: string[] = await caches.keys()
    for (const key of keys) {
      await caches.delete(key)
    }
    // retrieve all cookies
    const Cookies = document.cookie.split(';')
    // set past expiry to all cookies
    for (const element of Cookies) {
      document.cookie = element + '=; expires=' + new Date(0).toUTCString()
    }
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (const registration of registrations) {
          registration.unregister()
        }
      })
    }
    await instance.logout({
      account: instance.getActiveAccount(),
    })
  } catch (error) {
    const err = error as B2CError
    console.error(err)
    if (err?.errorMessage) {
      toast.error(err?.errorMessage)
    }
  }
}
